// Account-menu credit visibility — global storefront patch (no container).
//
// Two jobs, both on the storefront's account/nav menus:
//   1. Relabel the "Wallet" link to "Account Balance".
//   2. Show "Account Balance"/"Wallet" and "Request Credit" ONLY to a
//      logged-in B2B customer. Everyone else (B2C, other roles, logged out)
//      sees neither — the rows are hidden.
//
// "Am I B2B?" is the storefront FrontendUserRole, resolved server-side by the
// parts-finder at GET /api/credit/status (`roleKey`). The credit guard already
// fetches + caches that on window.__PF_CREDIT_STATUS_PROMISE__, so we reuse it
// (and fall back to fetching it ourselves when the guard hasn't run yet).
//
// Optional label overrides (comma-separated, case-insensitive) live on an
// optional [data-parts-account-menu] element rather than the old <script data-*>
// (there's only one bundle <script> now, not this widget's tag):
//   data-balance-labels="Wallet,Account Balance,My Wallet"
//   data-request-labels="Request Credit"
//
// It never throws and degrades to a no-op if the markup differs.

import { getTopDocument } from '../shared/dom.js';
import { readCookie, getSessionRef } from '../shared/identity.js';
import { apiJson } from '../shared/api.js';

function norm(s) { return String(s == null ? '' : s).toLowerCase().replace(/\s+/g, ' ').trim(); }

// Labels that identify each menu row (normalised). Overridable via data-* on an
// optional [data-parts-account-menu] element on the page.
function labelList(attr, defaults) {
  try {
    var cfg = getTopDocument().querySelector('[data-parts-account-menu]');
    var raw = cfg ? cfg.getAttribute(attr) : '';
    if (raw) {
      var parts = String(raw).split(',').map(norm).filter(Boolean);
      if (parts.length) return parts;
    }
  } catch (e) { /* ignore */ }
  return defaults;
}
var BALANCE_LABELS = labelList('data-balance-labels', ['wallet', 'my wallet', 'account balance']);
var REQUEST_LABELS = labelList('data-request-labels', ['request credit']);

// Resolve B2B once. Reuse the credit guard's cached /api/credit/status promise
// if it's already in flight; otherwise fetch it ourselves and cache it on the
// same global so the two scripts share one request.
var _b2bPromise = null;
function isB2b() {
  if (_b2bPromise) return _b2bPromise;
  var statusP = null;
  try { statusP = window.__PF_CREDIT_STATUS_PROMISE__; } catch (e) { statusP = null; }
  if (!statusP) {
    var headers = {};
    var jwt = readCookie('commerce_features'); if (jwt) headers['X-Commerce-Features'] = jwt;
    var sess = getSessionRef(); if (sess) headers['X-Session-Reference'] = sess;
    statusP = apiJson('/api/credit/status', { headers: headers }, null);
    try { window.__PF_CREDIT_STATUS_PROMISE__ = statusP; } catch (e) { /* ignore */ }
  }
  _b2bPromise = statusP
    .then(function (s) { return !!(s && String(s.roleKey || '').trim().toUpperCase() === 'B2B'); })
    .catch(function () { return false; });
  return _b2bPromise;
}

// null = not resolved yet; true / false once /api/credit/status answers.
var _isB2b = null;

function classify(anchor) {
  var t = norm(anchor.textContent);
  if (!t) return null;
  var i;
  for (i = 0; i < BALANCE_LABELS.length; i++) if (t === BALANCE_LABELS[i]) return 'balance';
  for (i = 0; i < REQUEST_LABELS.length; i++) if (t === REQUEST_LABELS[i]) return 'request';
  return null;
}
// Hide the whole menu row when there is one, else just the link.
function rowFor(anchor) { return anchor.closest('li') || anchor; }

// Swap the visible "Wallet" text for "Account Balance" while preserving any
// child elements (e.g. an <i> icon). Idempotent.
function relabelAnchor(anchor) {
  if (anchor.getAttribute('data-pf-acct-relabelled') === '1') return;
  var changed = false, i, n;
  for (i = 0; i < anchor.childNodes.length; i++) {
    n = anchor.childNodes[i];
    if (n.nodeType === 3 && /wallet/i.test(n.nodeValue)) {
      n.nodeValue = n.nodeValue.replace(/\bwallet\b/gi, 'Account Balance');
      changed = true;
    }
  }
  if (!changed && norm(anchor.textContent) === 'wallet') { anchor.textContent = 'Account Balance'; changed = true; }
  if (changed) anchor.setAttribute('data-pf-acct-relabelled', '1');
}

function hideRow(row) {
  if (row.getAttribute('data-pf-acct-hidden') === '1') return;
  row.setAttribute('data-pf-acct-hidden', '1');
  row.setAttribute('data-pf-acct-disp', row.style.display || '');
  row.style.display = 'none';
}
function showRow(row) {
  if (row.getAttribute('data-pf-acct-hidden') !== '1') return;
  row.style.display = row.getAttribute('data-pf-acct-disp') || '';
  row.removeAttribute('data-pf-acct-hidden');
  row.removeAttribute('data-pf-acct-disp');
}

function apply() {
  var anchors;
  try { anchors = getTopDocument().querySelectorAll('a'); } catch (e) { return; }
  for (var i = 0; i < anchors.length; i++) {
    var a = anchors[i];
    var kind = classify(a);
    if (!kind) continue;
    a.setAttribute('data-pf-acct', kind);
    var row = rowFor(a);
    if (_isB2b === true) {
      if (kind === 'balance') relabelAnchor(a);
      showRow(row);
    } else {
      // Not B2B, or role not yet known → hide. Hiding while undecided means a
      // non-B2B user never sees the links flash in.
      hideRow(row);
    }
  }
}

// Coalesced sweep — the account menu / header dropdown re-render on open and can
// render late, so we re-apply on DOM mutations.
var scheduled = false;
function schedule() {
  if (scheduled) return;
  scheduled = true;
  var run = function () { scheduled = false; try { apply(); } catch (e) { /* ignore */ } };
  if (window.requestAnimationFrame) window.requestAnimationFrame(run); else setTimeout(run, 16);
}

export function init() {
  // Sweep immediately (hide-first, before the role resolves), then resolve the
  // role and re-apply, then wire the observer to catch late/dropdown renders.
  try { apply(); } catch (e) { /* ignore */ }
  isB2b().then(function (v) { _isB2b = v; try { apply(); } catch (e) { /* ignore */ } });
  try {
    var doc = getTopDocument();
    new MutationObserver(schedule).observe(doc.documentElement || doc.body, { childList: true, subtree: true });
  } catch (e) { /* ignore */ }
}

export var id = 'account-menu';
